import { MaterialTableProps } from 'rs-material-table';
import React, { ReactElement } from 'react';
import { CustomColumn } from '../../../../../types/components/tables/tableTypes';
import CustomTable from '../../../../tables/CustomTable';
import { NoteData } from '../../../sharedComponents/notes/Notes';
import './NotesTable.css';

interface Props {
  tableData: NoteData[];
  useDates?: {
    startDate: string;
    endDate: string;
  };
  showToolbar: boolean;
  title: string;
  loadingHeight?: string;
  options?: MaterialTableProps<NoteData>['options'];
  includeSection?: boolean; // Optional - this allows us to include the section in the note data
}
const generateColumns = (
  includeSection: boolean = false,
): CustomColumn<NoteData>[] => {
  return [
    {
      title: 'Position Date',
      field: 'positionDate',
      width: 105,
      headerStyle: { textAlign: 'left', fontSize: '0.7rem', padding: '0.25rem' },
      cellStyle: { textAlign: 'left', verticalAlign: 'top', padding: '0.25rem' },
      render: (rowData: NoteData) => (
        <div className="text-xs font-medium text-gray-700 whitespace-nowrap">
          {rowData.positionDate}
        </div>
      ),
    },
    {
      title: 'Date Added',
      field: 'date',
      width: 110,
      render: (rowData: NoteData) => {
        const newDate = new Date(rowData.date);
        const formattedDate = newDate.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        });
        const formattedTime = newDate.toLocaleTimeString('en-GB', {
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'UTC',
        });
        return (
          <div className="text-left text-xs text-gray-900 whitespace-nowrap">
            {formattedDate} {formattedTime}
          </div>
        );
      },
      headerStyle: { textAlign: 'left', fontSize: '0.7rem', padding: '0.25rem' },
      cellStyle: { textAlign: 'left', verticalAlign: 'top', padding: '0.25rem' },
    },
    {
      title: 'Posted by',
      field: 'userName',
      width: 120,
      render: (rowData: NoteData) => (
        <div className="text-left">
          <div
            className="truncate text-xs font-medium text-gray-900"
            title={rowData.userName || undefined}
          >
            {rowData.userName?.trim() || '—'}
          </div>
        </div>
      ),
      headerStyle: { textAlign: 'left', fontSize: '0.7rem', padding: '0.25rem' },
      cellStyle: { textAlign: 'left', verticalAlign: 'top', padding: '0.25rem' },
    },
    includeSection && {
      title: 'Subject',
      field: 'subject',
      width: 140,
      render: (rowData: NoteData) => (
        <span className="block max-w-full truncate text-xs text-gray-900 whitespace-nowrap" title={rowData.subject || 'N/A'}>
          {rowData.subject || 'N/A'}
        </span>
      ),
      headerStyle: { textAlign: 'left', fontSize: '0.7rem', padding: '0.25rem' },
      cellStyle: { textAlign: 'left', verticalAlign: 'top', padding: '0.25rem' },
    },
    {
      title: 'Note',
      field: 'noteValue',
      headerStyle: { textAlign: 'left', fontSize: '0.7rem', padding: '0.25rem' },
      cellStyle: { textAlign: 'left', verticalAlign: 'top', padding: '0.25rem' },
      pdfWidth: 80,
      pdfRenderType: 'Table',
      render: (noteData: NoteData) => {
        if (noteData.noteType === 'table') {
          const tableDataString = noteData.noteValue.slice(
            noteData.noteValue.indexOf('{'),
          );
          const tableData = JSON.parse(tableDataString);
          return (
            <CustomTable<any>
              columns={tableData.columns}
              data={tableData.data}
              showToolbar={false}
              options={{ paging: false, search: false }}
            />
          );
        }
        return (
          <div className="text-xs text-gray-900 break-words">
            {noteData.noteValue}
          </div>
        );
      },
    },
  ].filter(Boolean) as CustomColumn<NoteData>[]; // ✅ make sure TS sees this as a clean array
};

function NotesTable(props: Props): ReactElement {

  const columns = generateColumns(props.includeSection);

  return (
    <div className="w-full">
      <div className="overflow-hidden rounded border border-mainblue bg-white">
        <div className={`notes-table-container${props.includeSection ? ' include-section' : ''}`}>
          <CustomTable<NoteData>
            showToolbar={props.showToolbar}
            title={props.title}
            loadingHeight={props.loadingHeight}
            columns={columns}
            options={{
              paging: false,
              exportButton: props.showToolbar,
              search: false,
              headerStyle: {
                backgroundColor: '#f9fafb',
                fontWeight: 600,
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: '#6b7280',
                borderBottom: '1px solid #e5e7eb',
              },
              rowStyle: {
                borderBottom: '1px solid #f3f4f6',
              },
              ...props.options,
            }}
            data={props.tableData}
          />
        </div>
      </div>
    </div>
  );
}

export default React.memo(NotesTable);
